// Need to use the React-specific entry point to import createApi
import { createApi } from '@reduxjs/toolkit/query/react'
import {dynamicBaseQuery} from "../badRequestHandler"

// ── Types ────────────────────────────────────────────────────────────────────

export interface TrainerRecord {
  _id?: string
  name?: string
  email?: string
  phoneNumber?: string
  status?: string
  branchId?: string
  specialization?: string
  experience?: number
  photo?: string
  [key: string]: unknown
}

// ── Request arg types ────────────────────────────────────────────────────────

export interface GetTrainersParams {
  page?:     number
  limit?:    number
  branchId?: string
  search?:   string
  status?:   string
  role?:     string
}

export interface UpdateTrainerArgs {
  id: string
  body: Partial<TrainerRecord>
}

export interface UpdateTrainerStatusArgs {
  id: string
  status: string
}

// ── Response types ───────────────────────────────────────────────────────────

export interface TrainersListResponse {
  trainers: TrainerRecord[]
  total?: number
  page?: number
}

export interface TrainerDetailResponse {
  trainer: TrainerRecord
}

// Define a service using a base URL and expected endpoints
export const trainer = createApi({
  reducerPath: 'trainer',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Trainers', 'TrainerIncentives'] as const,
  endpoints: (builder) => ({
    getTrainers: builder.query<TrainersListResponse, GetTrainersParams | undefined>({
      query: ({ page = 1, limit = 10, branchId, search, status, role } = {}) => {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (branchId && branchId !== 'all') params.append('branchId', branchId);
        if (search)  params.append('search', search);
        if (status)  params.append('status', status);
        if (role && role !== 'all') params.append('role', role);
        return { url: `/trainers?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Trainers'],
    }),
    getTrainersDetail: builder.query<TrainerDetailResponse, string>({
      query: (id) => ({
        url: `/trainers/${id}`,
        method: "GET",
      }),
      providesTags: ['Trainers'],
    }),
    addTrainers: builder.mutation<TrainerRecord, Partial<TrainerRecord>>({
      query: (newTrainer) => ({
        url: `/trainers`,
        method: "POST",
        body: newTrainer,
      }),
      invalidatesTags: ['Trainers'],
    }),
    updateTrainer: builder.mutation<TrainerRecord, UpdateTrainerArgs>({
      query: ({ id, body }) => ({
        url: `/trainers/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ['Trainers'],
    }),
    updateTrainerStatus: builder.mutation<TrainerRecord, UpdateTrainerStatusArgs>({
      query: ({ id, status }) => ({
        url: `/trainers/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ['Trainers'],
    }),
    verifyTrainer: builder.mutation<unknown, string>({
      query: (id) => ({
        url: `/trainers/${id}/verify`,
        method: "PATCH",
      }),
      invalidatesTags: ['Trainers'],
    }),
    unverifyTrainer: builder.mutation<unknown, string>({
      query: (id) => ({
        url: `/trainers/${id}/unverify`,
        method: "PATCH",
      }),
      invalidatesTags: ['Trainers'],
    }),
    // ── Incentive slabs ──
    getTrainerIncentives: builder.query<any, { trainerId: string; status?: string }>({
      query: ({ trainerId, status }) => ({
        url: status ? `/trainers/${trainerId}/incentives?status=${status}` : `/trainers/${trainerId}/incentives`,
        method: 'GET',
      }),
      providesTags: ['TrainerIncentives'],
    }),
    addTrainerIncentive: builder.mutation<any, { trainerId: string; slabs: { from: number; to: number; percentage: number }[] }>({
      query: ({ trainerId, ...body }) => ({
        url: `/trainers/${trainerId}/incentives`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TrainerIncentives'],
    }),
    getTrainerIncentiveSummary: builder.query<any, { trainerId: string; fromDate: string; toDate: string; clientId?: string }>({
      query: ({ trainerId, fromDate, toDate, clientId }) => ({
        url: `/trainers/${trainerId}/incentive-summary?fromDate=${fromDate}&toDate=${toDate}${clientId ? `&clientId=${clientId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['TrainerIncentives'],
    }),
    getTrainerClients: builder.query<any, { trainerId: string; fromDate: string; toDate: string }>({
      query: ({ trainerId, fromDate, toDate }) => ({
        url: `/trainers/${trainerId}/clients?fromDate=${fromDate}&toDate=${toDate}`,
        method: 'GET',
      }),
      providesTags: ['TrainerIncentives'],
    }),
    getTrainerIncentiveCalculate: builder.query<any, { trainerId: string; fromDate: string; toDate: string; tdsPercent?: number; gstPercent?: number }>({
      query: ({ trainerId, fromDate, toDate, tdsPercent, gstPercent }) => ({
        url: `/trainers/${trainerId}/incentive-calculate?fromDate=${fromDate}&toDate=${toDate}&tdsPercent=${tdsPercent ?? 0}&gstPercent=${gstPercent ?? 0}`,
        method: 'GET',
      }),
      providesTags: ['TrainerIncentives'],
    }),
    generateTrainerIncentive: builder.mutation<any, { trainerId: string; fromDate: string; toDate: string; tdsPercent: number; gstPercent: number; additionalFields?: { fieldName: string; amount: number; remark?: string }[]; deduction?: number; deductionRemark?: string }>({
      query: ({ trainerId, ...body }) => ({
        url: `/trainers/${trainerId}/incentive-generate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TrainerIncentives'],
    }),
    getTrainerIncentiveHistory: builder.query<any, { trainerId: string }>({
      query: ({ trainerId }) => ({
        url: `/trainers/${trainerId}/incentive-history`,
        method: 'GET',
      }),
      providesTags: ['TrainerIncentives'],
    }),
    // Cross-trainer incentive history list (not scoped to one trainer's detail page).
    getAllTrainersIncentiveHistory: builder.query<any, {
      page?: number; limit?: number; fromDate?: string; toDate?: string;
      trainerId?: string; search?: string; branchId?: string; status?: string;
    } | void>({
      query: (params) => {
        const { page, limit, fromDate, toDate, trainerId, search, branchId, status } = params || {};
        const sp = new URLSearchParams();
        if (page)    sp.append('page', String(page));
        if (limit)   sp.append('limit', String(limit));
        if (fromDate) sp.append('fromDate', fromDate);
        if (toDate)   sp.append('toDate', toDate);
        if (trainerId && trainerId !== 'all') sp.append('trainerId', trainerId);
        if (search)   sp.append('search', search);
        if (branchId && branchId !== 'all')   sp.append('branchId', branchId);
        if (status && status !== 'all')       sp.append('status', status);
        return {
          url: `/trainers/incentive-history?${sp.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['TrainerIncentives'],
    }),
    payTrainerIncentive: builder.mutation<any, { trainerId: string; incentiveId: string; paymentType: 'partial' | 'full'; amount: number; note?: string; deduction?: number; deductionRemark?: string }>({
      query: ({ trainerId, incentiveId, ...body }) => ({
        url: `/trainers/${trainerId}/incentive-history/${incentiveId}/pay`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TrainerIncentives'],
    }),
    toggleTrainerIncentiveStatus: builder.mutation<any, { trainerId: string; incentiveId: string; status?: string }>({
      query: ({ trainerId, incentiveId, status }) => ({
        url: `/trainers/${trainerId}/incentives/${incentiveId}/status`,
        method: 'PATCH',
        body: status ? { status } : {},
      }),
      invalidatesTags: ['TrainerIncentives'],
    }),

    // ── Trainer dashboard (own login → "me"; admin viewing a specific trainer → trainerId) ──
    getTrainerAddonExpiries: builder.query<any, { trainerId?: string } | undefined>({
      query: (args) => ({
        url: args?.trainerId ? `/trainers/${args.trainerId}/dashboard/addon-expiries` : `/trainers/me/dashboard/addon-expiries`,
        method: 'GET',
      }),
    }),
    getTrainerSalesGraph: builder.query<any, { trainerId?: string; month?: number; year?: number } | undefined>({
      query: (args) => {
        const { trainerId, month, year } = args || {};
        const params = new URLSearchParams();
        if (month) params.append('month', String(month));
        if (year)  params.append('year', String(year));
        const qs = params.toString();
        const base = trainerId ? `/trainers/${trainerId}/dashboard/sales-graph` : `/trainers/me/dashboard/sales-graph`;
        return { url: qs ? `${base}?${qs}` : base, method: 'GET' };
      },
    }),

    getOpenTrainers: builder.query<TrainersListResponse, GetTrainersParams | undefined>({
      query: ({ page = 1, limit = 100, branchId } = {}) => {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (branchId) params.append('branchId', branchId);
        return {
          url: `/trainers/open?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ['Trainers'],
    }),
  }),
})

export const { useGetTrainersQuery, useAddTrainersMutation, useGetTrainersDetailQuery, useUpdateTrainerMutation, useUpdateTrainerStatusMutation, useVerifyTrainerMutation, useUnverifyTrainerMutation, useGetOpenTrainersQuery, useGetTrainerIncentivesQuery, useAddTrainerIncentiveMutation, useToggleTrainerIncentiveStatusMutation, useGetTrainerIncentiveSummaryQuery, useGetTrainerClientsQuery, useGetTrainerIncentiveCalculateQuery, useLazyGetTrainerIncentiveCalculateQuery, useGenerateTrainerIncentiveMutation, useGetTrainerIncentiveHistoryQuery, useGetAllTrainersIncentiveHistoryQuery, usePayTrainerIncentiveMutation, useGetTrainerAddonExpiriesQuery, useGetTrainerSalesGraphQuery } = trainer
